import React, { useEffect, useState } from "react";
import ButtonComponent from "@components/ButtonComponent";
import { useTranslation } from "react-i18next";

import { ONE_VALUE } from "@utils/constants";
import { X, Trash2 } from "lucide-react";

const SINGLE_INFO = 1;

const EditPopup = ({
  newData,
  attributes,
  inputValue,
  handleInputChange,
  handleAttributeInput,
  handleRemoveAttribute,
  handleSave,
  setPopupOpen,
  isNew = false,
  hasHeader = false,
  parent = null,
  parentName = "",
  type,
}) => {
  const { t: translate, i18n } = useTranslation();

  const [isSaveEnabled, setIsSaveEnabled] = useState(false);

  useEffect(() => {
    if (isNew) {
      if (
        !newData.OrgContacts?.some(
          (contact) => contact.ContactTypeTag === "MASTERMOBILE"
        )
      ) {
        handleInputChange("OrgContacts", [
          ...(newData.OrgContacts || []),
          { ContactTypeTag: "MASTERMOBILE", ContactValue: "", isValid: true },
        ]);
      }
      if (
        !newData.OrgContacts?.some(
          (contact) => contact.ContactTypeTag === "MASTEREMAIL"
        )
      ) {
        handleInputChange("OrgContacts", [
          ...(newData.OrgContacts || []),
          { ContactTypeTag: "MASTEREMAIL", ContactValue: "", isValid: true },
        ]);
      }

      if (!newData.OrgAddresses?.length) {
        handleInputChange("OrgAddresses", [
          {
            AddressTypeTag: "MAINADDRESS",
            AddressDetails: [
              { LanguageCode: "en", StreetAddress: "" },
              { LanguageCode: "ar", StreetAddress: "" },
            ],
          },
        ]);
      }
    }
    const areOrgDetailsFilled = newData?.OrgDetails?.every((detail) =>
      detail.ShortName?.trim()
    );
    const areOrgContactsValid = newData?.OrgContacts?.every((contact) =>
      contact.ContactValue?.trim()
    );
    const areOrgAddressesFilled = newData?.OrgAddresses?.every((address) =>
      address.AddressDetails?.every((detail) => detail.StreetAddress?.trim())
    );

    setIsSaveEnabled(
      areOrgDetailsFilled && areOrgContactsValid && areOrgAddressesFilled
    );
  }, [isNew, newData]);

  return (
    <div data-testid="manage-popup" className="">
      <h2 className="text-lg dark:text-white capitalize text-center font-bold mb-4">
        {isNew ? `Add new ${type} ` : `Edit ${type} `}
      </h2>
      {hasHeader && (
        <div className="mb-4 bg-gray-100 p-4 rounded-lg dark:bg-gray-900 dark:text-white">
          <div className="flex items-center gap-[30%]">
            {parent.OrgDetails?.map((detail, index) => (
              <div
                data-testid={`header-${
                  detail.ShortName
                }-${detail.LanguageCode.toUpperCase()}`}
                key={index}
                className="text-sm flex flex-col gap-2 dark:text-white"
              >
                <div className="font-semibold dark:text-white">
                  {parentName} {translate("hierarchy.edit-popup.name")} {"("}
                  {detail.LanguageCode.toUpperCase()}
                  {")"}
                </div>
                <div>{detail.ShortName || "N/A"}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mb-4">
        {newData?.OrgDetails?.map((detail, index) => (
          <div key={index} className="mb-4 flex flex-col gap-2">
            <label className="block capitalize text-sm font-semibold dark:text-white">
              {type} {translate("hierarchy.edit-popup.name")} {"("}
              {detail.LanguageCode.toUpperCase()}
              {")"}
            </label>
            <input
              data-testid={`branch-name-${detail.LanguageCode.toUpperCase()}`}
              type="text"
              value={detail.ShortName || ""}
              onChange={(e) => {
                const updatedDetails = [...newData.OrgDetails];
                updatedDetails[index] = {
                  ...updatedDetails[index],
                  ShortName: e.target.value,
                };
                handleInputChange("OrgDetails", updatedDetails);
                isNew &&
                  handleInputChange("OrgExternalReference", e.target.value);
              }}
              className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
            />
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-2">
        <label className="block text-sm font-semibold dark:text-white">
          {translate("hierarchy.edit-popup.branchCodesLabel")}
        </label>
        <div className="flex flex-wrap gap-2 p-2 border rounded">
          {attributes?.map((singleAttribute, index) => (
            <div
              key={index}
              className="flex items-center gap-2 px-2 py-1 bg-gray-200 rounded dark:bg-gray-700 dark:text-white "
            >
              <span data-testid={`branch-code-${singleAttribute}`}>
                {singleAttribute}
              </span>
              <button
                className="text-red-500 hover:text-red-700"
                onClick={() => handleRemoveAttribute(index, singleAttribute)}
              >
                <X size={18} />
              </button>
            </div>
          ))}
          <input
            data-testid="branch-code-input"
            type="text"
            value={inputValue}
            onChange={handleAttributeInput}
            placeholder="Add new code..."
            className="flex-grow p-2 border-none outline-none dark:bg-gray-700 dark:text-white dark:border-gray-600"
          />
        </div>
      </div>
      <div className="my-4">
        <h4 className="text-md font-semibold mb-2 dark:text-white">
          {translate("hierarchy.edit-popup.mobileNumbersLabel")}
        </h4>
        {newData?.OrgContacts?.filter(
          (contact) => contact.ContactTypeTag === "MASTERMOBILE"
        ).map((contact, index) => {
          const phoneRegex = /^[0-9]{10,15}$/;
          const isPhoneValid = contact.ContactValue
            ? phoneRegex.test(contact.ContactValue)
            : contact.isValid;
          return (
            <div key={index} className="mb-4 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <label className="block capitalize text-sm font-semibold dark:text-white">
                  {translate("hierarchy.edit-popup.mobileNumberInputLabel")}{" "}
                  {index + ONE_VALUE}
                </label>
                {newData.OrgContacts.filter(
                  (c) => c.ContactTypeTag === "MASTERMOBILE"
                ).length > SINGLE_INFO && (
                  <button
                    className="text-red-500 hover:text-red-700"
                    onClick={() => {
                      const updatedContacts = newData.OrgContacts.filter(
                        (c) => c !== contact
                      );
                      handleInputChange("OrgContacts", updatedContacts);
                    }}
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  data-testid={`mobile-number-${index + ONE_VALUE}`}
                  type="text"
                  value={contact.ContactValue || ""}
                  onChange={(e) => {
                    const isValid = phoneRegex.test(e.target.value);
                    const updatedContacts = newData.OrgContacts.map((c) =>
                      c.ContactTypeTag === "MASTERMOBILE" && c === contact
                        ? { ...c, ContactValue: e.target.value, isValid }
                        : c
                    );
                    handleInputChange("OrgContacts", updatedContacts);
                  }}
                  className={`w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    !isPhoneValid ? "border-red-500" : ""
                  }`}
                />
              </div>
              {!isPhoneValid && (
                <p
                  data-testid="invalid-phone-number"
                  className="text-red-500 text-sm"
                >
                  {translate("hierarchy.edit-popup.invalidPhoneNumberError")}
                </p>
              )}
            </div>
          );
        })}
        <button
          className="text-blue-500 hover:text-blue-700"
          onClick={() => {
            const updatedContacts = [
              ...(newData.OrgContacts || []),
              {
                ContactTypeTag: "MASTERMOBILE",
                ContactValue: "",
                isValid: true,
              },
            ];
            handleInputChange("OrgContacts", updatedContacts);
          }}
        >
          {translate("hierarchy.edit-popup.addMobileNumberButton")}
        </button>

        <h4 className="text-md font-semibold mt-4 mb-2 dark:text-white">
          {translate("hierarchy.edit-popup.emailsLabel")}
        </h4>
        {newData?.OrgContacts?.filter(
          (contact) => contact.ContactTypeTag === "MASTEREMAIL"
        ).map((contact, index) => {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const isEmailValid = contact.ContactValue
            ? emailRegex.test(contact.ContactValue)
            : contact.isValid;
          return (
            <div
              key={index}
              className="mb-4 flex flex-col gap-2 dark:text-white"
            >
              <div className="flex items-center justify-between">
                <label className="block capitalize text-sm font-semibold">
                  {translate("hierarchy.edit-popup.emailInputLabel")}{" "}
                  {index + ONE_VALUE}
                </label>
                {newData.OrgContacts.filter(
                  (c) => c.ContactTypeTag === "MASTEREMAIL"
                ).length > SINGLE_INFO && (
                  <button
                    className="text-red-500 hover:text-red-700"
                    onClick={() => {
                      const updatedContacts = newData.OrgContacts.filter(
                        (c) => c !== contact
                      );
                      handleInputChange("OrgContacts", updatedContacts);
                    }}
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  data-testid={`email-${index + ONE_VALUE}`}
                  type="text"
                  value={contact.ContactValue || ""}
                  onChange={(e) => {
                    const isValid = emailRegex.test(e.target.value);

                    const updatedContacts = newData.OrgContacts.map((c) =>
                      c.ContactTypeTag === "MASTEREMAIL" && c === contact
                        ? { ...c, ContactValue: e.target.value, isValid }
                        : c
                    );
                    handleInputChange("OrgContacts", updatedContacts);
                  }}
                  className={`w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                    !isEmailValid ? "border-red-500" : ""
                  }`}
                />
              </div>
              {!isEmailValid && (
                <p data-testid="invalid-email" className="text-red-500 text-sm">
                  {translate("hierarchy.edit-popup.invalidEmailError")}
                </p>
              )}
            </div>
          );
        })}
        <button
          className="text-blue-500 hover:text-blue-700"
          onClick={() => {
            const updatedContacts = [
              ...(newData.OrgContacts || []),
              {
                ContactTypeTag: "MASTEREMAIL",
                ContactValue: "",
                isValid: true,
              },
            ];
            handleInputChange("OrgContacts", updatedContacts);
          }}
        >
          {translate("hierarchy.edit-popup.addEmailButton")}
        </button>
      </div>

      <div className="mb-4">
        <h4 className="text-md font-semibold mt-4 mb-2 dark:text-white">
          {translate("hierarchy.edit-popup.addressesLabel")}
        </h4>
        {newData?.OrgAddresses?.map((address, index) => (
          <div key={index} className="mb-4 flex flex-col gap-2 dark:text-white">
            {address.AddressDetails?.map((detail, detailIndex) => (
              <div key={detailIndex} className="mb-2">
                <label className="block text-sm font-semibold">
                  {detail.LanguageCode.toUpperCase()}{" "}
                  {translate("hierarchy.edit-popup.addressInputLabel")}
                </label>
                <input
                  data-testid={`address-${detail.LanguageCode.toUpperCase()}`}
                  type="text"
                  value={detail.StreetAddress || ""}
                  onChange={(e) => {
                    const updatedAddresses = [...newData.OrgAddresses];
                    updatedAddresses[index].AddressDetails[detailIndex] = {
                      ...updatedAddresses[index].AddressDetails[detailIndex],
                      StreetAddress: e.target.value,
                      AddressTypeName: "MAINADDRESS",
                      City: e.target.value,
                      CountryCode: null,
                      CountryName: null,
                      District: e.target.value,
                      LanguageCode: detail.LanguageCode,
                      Sector: e.target.value,
                    };
                    handleInputChange("OrgAddresses", updatedAddresses);
                  }}
                  className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
                />
              </div>
            ))}
          </div>
        ))}
        {newData.OrgAddresses.length < SINGLE_INFO && (
          <button
            className="text-blue-500 hover:text-blue-700"
            onClick={() => {
              const updatedAddresses = [
                ...(newData.OrgAddresses || []),
                {
                  AddressTypeTag: "MAINADDRESS",
                  AddressDetails: [
                    { LanguageCode: "en", StreetAddress: "" },
                    { LanguageCode: "ar", StreetAddress: "" },
                  ],
                },
              ];
              handleInputChange("OrgAddresses", updatedAddresses);
            }}
          >
            {translate("hierarchy.edit-popup.addAddressButton")}
          </button>
        )}
      </div>
      <div className="flex justify-between gap-2">
        <ButtonComponent
          type="cancel"
          color="red"
          onClick={() => setPopupOpen(false)}
          text={"Cancel"}
          className="mt-5 w-full"
        />
        <ButtonComponent
          data-testid="save-button"
          color="green"
          onClick={handleSave}
          text={isNew ? "Add Branch" : "Save"}
          className="mt-5 w-full"
          disabled={!isSaveEnabled}
        />
      </div>
    </div>
  );
};

export default EditPopup;
